// src/hooks/use-monday-import-gap.ts
// VIB-777: Henter import-gap-analyse fra Monday for én ansatt + ett år.
import { useQuery } from "@tanstack/react-query";
import { SUPABASE_FUNCTIONS_URL, SUPABASE_ANON_KEY } from "@/lib/supabase";

export type GroupCategory =
  | "project"
  | "intern"
  | "tilbud"
  | "pitch"
  | "treatment"
  | "ferie"
  | "avspasering"
  | "fri"
  | "timer_til_gode"
  | "other";

export interface MissingSession {
  id: string;
  started_at: string;
  ended_at: string;
  seconds: number;
}

export interface GapItem {
  itemId: number;
  name: string;
  groupId: string;
  groupTitle: string;
  groupCategory: GroupCategory;
  sessionsCount: number;
  totalSeconds: number;
  projectCode: string | null;
  parsedPhase: string | null;
  parsedTask: string;
  imported: boolean;
  /** VIB-940: session-nivå gap-detaljer. */
  missingSessionsCount?: number;
  missingSeconds?: number;
  missingSessions?: MissingSession[];
}

export interface GapGroup {
  groupId: string;
  title: string;
  category: GroupCategory;
  items: GapItem[];
}

export interface ProjectAggregate {
  projectCode: string | null;
  projectName: string | null;
  totalItems: number;
  importedItems: number;
  gapItems: number;
  totalSecondsInGap: number;
}

export interface GapResponse {
  success: boolean;
  summary: {
    totalItems: number;
    importedItems: number;
    gapItems: number;
    totalSecondsInGap: number;
  };
  projectAggregate: ProjectAggregate[];
  groups: GapGroup[];
  error?: string;
}

export function useMondayImportGap(
  userId: string | null,
  year: number,
  enabled: boolean,
) {
  return useQuery<GapResponse>({
    queryKey: ["monday-import-gap", { userId, year }],
    enabled: !!userId && enabled,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const res = await fetch(
        `${SUPABASE_FUNCTIONS_URL}/analyze-monday-import-gap`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            apikey: SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ userId, year }),
        },
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`analyze-monday-import-gap (${res.status}): ${text.slice(0, 300)}`);
      }
      const json = (await res.json()) as GapResponse;
      if (!json.success) throw new Error(json.error ?? "Ukjent feil");
      return json;
    },
  });
}
